import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import type { MetricValue, ScreenerColumn, ScreenerUnit } from '@/types/screener';

const MINUS = '−';
const LAKH_CRORE = 100_000;

const rupees = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const wholeNumber = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const plainNumber = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });

const signOf = (value: number) => (value > 0 ? '+' : value < 0 ? MINUS : '');

// One crore scheme everywhere: ₹16.3L Cr for lakh-crore companies, ₹4,312 Cr, ₹45.6 Cr for small ones.
export function formatCrore(value: number): string {
  const sign = value < 0 ? MINUS : '';
  const abs = Math.abs(value);
  if (abs >= LAKH_CRORE) return `${sign}₹${(abs / LAKH_CRORE).toFixed(1)}L Cr`;
  if (abs >= 100) return `${sign}₹${wholeNumber.format(abs)} Cr`;
  return `${sign}₹${abs.toFixed(1)} Cr`;
}

type MetricColumn = Pick<ScreenerColumn, 'key' | 'unit' | 'change'>;

const DISTANCE_BELOW_HIGH = 'high_52w_distance_pct';

function formatMagnitude(value: number, unit: ScreenerUnit): string {
  switch (unit) {
    case 'inr':
      return `₹${rupees.format(value)}`;
    case 'inr_cr':
      return formatCrore(value);
    case 'pct':
      return `${value.toFixed(2)}%`;
    case 'pp':
      return `${value.toFixed(2)} pp`;
    case 'ratio':
      return `${value.toFixed(2)}×`;
    case 'index':
      return value.toFixed(1);
    case 'score':
      return wholeNumber.format(value);
    default:
      return plainNumber.format(value);
  }
}

// Changes are always signed; levels only carry a minus when negative (e.g. a cash outflow).
export function formatMetric(value: MetricValue, column: MetricColumn): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return humanizeText(value);

  if (column.key === DISTANCE_BELOW_HIGH) {
    const below = value.toFixed(1);
    return below === '0.0' ? 'At high' : `${below}% below`;
  }
  const sign = column.change ? signOf(value) : value < 0 ? MINUS : '';
  return `${sign}${formatMagnitude(Math.abs(value), column.unit)}`;
}

// Unsigned, compact form for filter chips ("ROE ≥ 15%").
export function formatBound(value: number, unit: ScreenerUnit): string {
  switch (unit) {
    case 'inr':
      return `₹${plainNumber.format(value)}`;
    case 'inr_cr':
      return formatCrore(value);
    case 'pct':
      return `${plainNumber.format(value)}%`;
    case 'pp':
      return `${plainNumber.format(value)} pp`;
    case 'ratio':
      return `${plainNumber.format(value)}×`;
    default:
      return plainNumber.format(value);
  }
}

export function metricTone(value: MetricValue, column: MetricColumn): string {
  if (value === null || value === undefined) return 'text-zinc-400 dark:text-zinc-600';
  if (typeof value !== 'number' || !column.change) return 'text-zinc-800 dark:text-zinc-200';
  if (value > 0) return 'text-emerald-600 dark:text-emerald-400';
  if (value < 0) return 'text-red-600 dark:text-red-400';
  return 'text-zinc-600 dark:text-zinc-400';
}

export function scoreTone(score: number): { bar: string; text: string } {
  if (score >= 70) return { bar: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-400' };
  if (score >= 40) return { bar: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-400' };
  return { bar: 'bg-red-500', text: 'text-red-700 dark:text-red-400' };
}

export function humanizeText(value: string): string {
  const spaced = value.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function formatCount(value: number): string {
  return wholeNumber.format(value);
}

function parseTimestamp(timestamp: string | null | undefined): Date | null {
  if (!timestamp) return null;
  const date = parseISO(timestamp);
  return isValid(date) ? date : null;
}

export function timeAgo(timestamp: string | null | undefined): string | null {
  const date = parseTimestamp(timestamp);
  return date ? formatDistanceToNow(date, { addSuffix: true }) : null;
}

export function formatDateTime(timestamp: string | null | undefined): string | null {
  const date = parseTimestamp(timestamp);
  return date ? format(date, 'd MMM yyyy, h:mm a') : null;
}

export function formatTime(timestamp: string | null | undefined): string | null {
  const date = parseTimestamp(timestamp);
  return date ? format(date, 'h:mm a') : null;
}

export function formatDayHeading(isoDate: string): string {
  const date = parseISO(isoDate);
  if (!isValid(date)) return isoDate;
  const today = format(new Date(), 'yyyy-MM-dd');
  const yesterday = format(new Date(Date.now() - 86_400_000), 'yyyy-MM-dd');
  if (isoDate === today) return 'Today';
  if (isoDate === yesterday) return 'Yesterday';
  return format(date, 'EEEE, d MMMM yyyy');
}

export const stockHref = (exchange: string, symbol: string) =>
  `/ai-scans/stocks/${encodeURIComponent(exchange)}/${encodeURIComponent(symbol)}`;

const LODR_PREFIX = /^Announcement under Regulation 30 \(LODR\)\s*-\s*/i;
const MAX_CATEGORY_LENGTH = 60;

export interface AnnouncementText {
  category: string | null;
  title: string;
  details: string | null;
}

// Exchange filings read "Category - details" and NSE titles start with a legal prefix; splitting
// them gives a scannable label, a plain title and a summary that doesn't repeat the title.
export function describeAnnouncement(title: string, description: string | null): AnnouncementText {
  const cleanTitle = title.replace(LODR_PREFIX, '').trim() || title;
  // BSE descriptions glue the name to the verb: "Kotyark Industries Ltdhas informed BSE…".
  const text = description?.trim().replace(/\b(Ltd|Limited)has\b/g, '$1 has') ?? '';
  const separator = text.indexOf(' - ');
  const hasCategory = separator > 0 && separator <= MAX_CATEGORY_LENGTH;
  const category = hasCategory ? text.slice(0, separator).trim() : null;
  const details = (hasCategory ? text.slice(separator + 3) : text).trim();
  const repeatsTitle = !details || cleanTitle.toLowerCase().includes(details.toLowerCase());
  return {
    category: category && category.toLowerCase() !== 'general' ? category : null,
    title: cleanTitle,
    details: repeatsTitle ? null : details,
  };
}
